<template>
	<div class="login-root flex flex-1 w-screen h-screen">
		<div v-if="loading" class="flex h-full w-full align-items-center justify-content-center">
			<Loading />
		</div>
		<div v-else class="login-shell" :class="{ 'login-shell--coletor': isColetorLogin }">
			<!--
				Águia no background (desktop e coletor) — sem dividir a tela.
				Metade esquerda; borda direita no meio da tela; centro na altura.
			-->
			<img
				class="login-bg-eagle"
				src="~/assets/images/harpia-silhueta-aguia.png"
				alt=""
				aria-hidden="true"
			>

			<div v-focustrap class="login-body">
				<div class="login-card">
					<div class="login-panel-title">
						<div class="login-brand-row">
							<img
								class="login-logo"
								src="~/assets/images/logo-harpia-wms.png"
								alt="Harpia WMS"
							>
							<span v-if="appVersion" class="login-version">v{{ appVersion }}</span>
						</div>
					</div>

					<div class="login-panel-content">
						<div class="login-fields">
							<div class="login-field username-container">
								<label for="inputUsuario">Usuário</label>
								<div class="user-input">
									<R3InputNumber v-model="userID" :ref="loginForm.inputUser" input-i-d="inputUsuario" mode="numeric"
										:passthrough="{ root: { id: 'inputUsuario' } }" :debounce="true" />
								</div>
							</div>

							<div class="login-field password-container">
								<label for="inputSenha">Senha</label>
								<div class="password-input">
									<R3InputNumber :ref="loginForm.inputPassword" input-i-d="inputSenha" mode="numeric" :feed-back="false"
										:passthrough="{ root: { id: 'inputSenha' } }" password />
								</div>
							</div>

							<div class="login-field company-container">
								<label for="dropdown">Empresa</label>
								<!--
									capture: intercepta Enter antes do PrimeVue.
									Com empresa já escolhida → foco no Entrar.
									Sem seleção → deixa o Dropdown abrir as opções.
								-->
								<div class="company-dropdown" @keydown.capture="onEmpresaKeydownCapture">
									<Dropdown
										id="dropdown"
										ref="companyDropdownRef"
										v-model="companySelected"
										class="w-full"
										:options="userStore.companiesNames"
										:disabled="disableDropDown"
										placeholder="Nenhuma empresa selecionada"
									/>
								</div>
							</div>

							<div id="disable_keyboard_checkbox" class="login-field field-checkbox login-field-row">
								<Checkbox v-model="disabledKeyboard" input-id="disableKeyboard" :binary="true" />
								<label for="disableKeyboard" class="ml-2">Desativar teclado</label>
							</div>

							<div class="login-field login-actions login-field-row">
								<div ref="loginButtonRef" class="login-actions-main">
									<Button id="login-button" label="Entrar" icon="pi pi-check" class="w-full" @click="authenticate" />
								</div>
								<div v-if="deviceStore.isCapacitor">
									<Button id="configModal" icon="pi pi-cog" @click="showLoginConfigModal" />
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Fora do card, abaixo e centralizado -->
				<div class="login-powered">
					<span class="login-powered-text">powered by</span>
					<button
						type="button"
						class="p-0 border-none bg-transparent cursor-pointer"
						:disabled="verificandoComunicacao"
						aria-label="Verificar comunicação com o servidor"
						@click="verificarComunicacao"
					>
						<img
							class="login-powered-logo"
							src="~/assets/images/logo-preferencial.png"
							alt="R3 Sistemas"
						>
					</button>
				</div>
			</div>
			<div v-if="showErrorModal === true" id="errorModal">
				<FailMessage :header="errorModalObject.statusCode" :message="errorModalObject.error" :visible="showErrorModal"
					@fail-login-modal-dismiss="onModalDismiss" />
			</div>

			<ConnectionConfig v-model:visible="connectionConfigVisible" @close="hideLoginConfigModal" />

			<FailMessage :visible="showUnauthorizedUserAlert" header="Sessão expirada"
				message="Faça o login novamente, tempo limite atingido." @fail-login-modal-dismiss="failModalDismiss" />
		</div>
	</div>
</template>

<script lang="ts" setup>
	// import { AxiosError } from "axios";
	import { onBeforeUnmount, onMounted, nextTick } from "vue"; // Removed onBeforeMount
	import { useDeviceStore } from "~/store/device/deviceStore";
	import type {
		UserCompaniesAutorization,
		userAuthResponse,
	} from "~/types/login";
	import { LoginForm } from "~/classes/LoginForm";

	const connectionConfigVisible = ref<boolean>(false);


	const loginForm = new LoginForm(); // Inicializar diretamente aqui

	const deviceStore = useDeviceStore();
	const config = useRuntimeConfig();
	const { $r3fetchdata } = useNuxtApp();
	const { getAccessTokenExpiration, scheduleExpirationFromToken, syncSessionFromToken } = useSessionManager();
	const loading = ref(true);

	/** Versão publicada em `runtimeConfig.public.VERSION` (package.json). */
	const appVersion = computed(() => config?.public?.VERSION ?? "");

	/**
	 * Login no coletor (mobile / nativo): fundo em degradê cinza suave
	 * para destacar o painel branco.
	 */
	const isColetorLogin = computed(() => {
		return Boolean(
			deviceStore.isMobile ||
			deviceStore.isNative ||
			deviceStore.isCapacitor,
		);
	});
	const companiesNames = ref([]);
	const idDebounceTimeInMS = 1000;
	const { msgDlg } = useMessageDlg();
	const verificandoComunicacao = ref(false);

	const showErrorModal = ref(false);
	const errorModalObject = ref({
		statusCode: "",
		error: "",
	});

	const companies = ref<string[]>([]);
	const disableDropDown = ref(true);
	const companySelected = ref("");

	/**
	 * Última empresa do login (cookie, 1 ano).
	 * Preferimos o ID (estável); o nome fica de fallback se a lista mudar.
	 */
	type UltimaEmpresaCookie = {
		id: string;
		nome: string;
	};

	const ultimaEmpresaCookie = useCookie<UltimaEmpresaCookie | string | null>(
		"harpia_ultima_empresa",
		{
			maxAge: 60 * 60 * 24 * 365,
			sameSite: "lax",
			path: "/",
		},
	);

	// Dispositivo
	const disabledKeyboard = ref(deviceStore.desativarTeclado);

	watch(disabledKeyboard, () => {
		deviceStore.setDesativarTeclado(disabledKeyboard.value);
	});

	// Persiste a empresa assim que o operador escolhe no dropdown
	watch(companySelected, (nome) => {
		if (nome) {
			gravarUltimaEmpresa(nome);
		}
	});

	/**
	 * Valor do campo Usuário usado pela tela de login.
	 *
	 * O vínculo direto evita depender da propriedade exposta do R3InputNumber,
	 * que pode não disparar o watcher da página em alguns coletores Android.
	 */
	const userID = ref<number | null>(null);
	let userInputElement: HTMLInputElement | null = null;
	// const debouncedUserID = useDebounce(userID, idDebounceTimeInMS);

	// const userID = useDebounce(userID, 1000)

	const password = ref("");
	// let checked = ref(false);

	// Stores
	const pageStore = usePageStore();
	const userStore = useUserStore();
	// const systemStore = useSystemStore();

	definePageMeta({
		layout: "empty",
		// middleware: "auth"
	});

	// Lifecycles

	onMounted(async () => {

		if (userStore.authenticationLogout === true) {
			showUnauthorizedUserAlert.value = true;
			// await msgDlg("Autorização", "informa", "Autorização expirada, faça novamente o login", ["OK"]);
			userStore.setAuthenticationLogout(false);
		}

		pageStore.setCurrentPage("Login");
		loading.value = false;

		await nextTick(); // formulário sai do v-if="loading"
		await nextTick(); // refs dos R3InputNumber disponíveis

		// Em alguns coletores, o InputNumber mostra o valor no campo nativo, mas
		// não propaga o evento do componente a tempo para o vínculo da página.
		// O listener nativo garante que o login acompanhe tanto teclado quanto leitor.
		userInputElement = document.getElementById("inputUsuario") as HTMLInputElement | null;
		userInputElement?.addEventListener("input", sincronizarUsuarioComCampo);

		// Foco no campo Usuário assim que a tela abre (coletor e desktop)
		focarCampoUsuario();

		// Configure o debounce aqui, após o componente estar montado
		// e após nextTick para garantir que a ref do R3Input esteja disponível.

		let timer: any;
		watch(userID, (newVal) => {
			clearTimeout(timer);
			timer = setTimeout(async () => {
				if (newVal !== null) await searchCompanies();
			}, 1000);
		});
	});

	onBeforeUnmount(() => {
		userInputElement?.removeEventListener("input", sincronizarUsuarioComCampo);
	});

	/** Sincroniza o valor digitado no input nativo com a busca de empresas. */
	function sincronizarUsuarioComCampo(event: Event) {
		const valorDigitado = (event.target as HTMLInputElement | null)?.value?.trim() ?? "";
		userID.value = valorDigitado === "" ? null : Number(valorDigitado);
	}

	/**
	 * Coloca o foco no input de usuário (R3InputNumber).
	 * Tenta a API do componente e, se ainda não montou, o DOM pelo id.
	 */
	function focarCampoUsuario() {
		const comp = loginForm.inputUser.value as { setFocus?: () => void } | null;
		if (comp?.setFocus) {
			comp.setFocus();
			return;
		}
		const el = document.getElementById("inputUsuario") as HTMLInputElement | null;
		el?.focus();
	}

	/** Ref do Dropdown de empresa (PrimeVue) — overlayVisible + show(). */
	const companyDropdownRef = ref<{
		overlayVisible?: boolean;
		show?: (isFocus?: boolean) => void;
		$el?: HTMLElement;
	} | null>(null);

	function isEnterKey(e: KeyboardEvent): boolean {
		return (
			e.key === "Enter"
			|| e.key === "NumpadEnter"
			|| e.code === "Enter"
			|| e.code === "NumpadEnter"
			|| (e as KeyboardEvent & { keyCode?: number }).keyCode === 13
			|| (e as KeyboardEvent & { which?: number }).which === 13
		);
	}

	function focarBotaoEntrar() {
		const btn = document.getElementById("login-button") as HTMLButtonElement | null;
		btn?.focus();
	}

	/**
	 * Enter no list de empresa (coletor):
	 * - lista aberta → deixa o PrimeVue confirmar a opção / fechar
	 * - tem empresa selecionada e lista fechada → passa o foco para Entrar
	 * - sem seleção e lista fechada → abre as opções
	 */
	function onEmpresaKeydownCapture(e: KeyboardEvent) {
		if (!isEnterKey(e)) {
			return;
		}

		const dd = companyDropdownRef.value;
		const listaAberta = Boolean(dd?.overlayVisible);

		if (listaAberta) {
			return;
		}

		if (companySelected.value) {
			e.preventDefault();
			e.stopPropagation();
			focarBotaoEntrar();
			return;
		}

		// Sem seleção: garante abertura das opções (PrimeVue também abre no Enter;
		// reforçamos caso o evento seja engolido em algum coletor).
		if (dd?.show && !disableDropDown.value) {
			e.preventDefault();
			e.stopPropagation();
			dd.show(true);
		}
	}

	// onBeforeUnmount(async () => {
	// });

	// watch(userID, useDebounce<void>(async (current, previous) => {

	// 	await searchCompanies()

	// }, 1000));

	// watch(debouncedUserID, async () => {
	// 	// await searchCompanies();
	// });

	function onModalDismiss() {
		showErrorModal.value = false;
	}

	type PingResponse = {
		responseTime: { ping: number; sqlResponse: number };
	};

	type ServerDateResponse = {
		DATA: string;
	};

	/** Verifica o caminho Login → API → banco e exibe a hora do banco. */
	async function verificarComunicacao() {
		if (verificandoComunicacao.value) {
			return;
		}

		verificandoComunicacao.value = true;
		try {
			const [ping, data] = await Promise.all([
				$r3fetchdata.get<PingResponse>("/api/utils/ping"),
				$r3fetchdata.get<ServerDateResponse>("/api/utils/data"),
			]);

			await msgDlg(
				"Comunicação",
				"informa",
				`Comunicação OK.\nData e hora do banco: ${data.DATA}\nResposta do banco: ${ping.responseTime.sqlResponse} ms.`,
				["OK"],
			);
		}
		catch {
			await msgDlg(
				"Comunicação",
				"erro",
				"Houve erro na comunicação com o servidor.",
				["OK"],
			);
		}
		finally {
			verificandoComunicacao.value = false;
		}
	}

	async function searchCompanies() {
		const companiesNames: string[] = [];
		const companiesID: string[] = [];
		// throw new Error('teste');
		console.log("O user id foi: ", userID.value);


		const data = await $r3fetchdata.get<UserCompaniesAutorization, unknown>(
			"/api/usuarios/empresasdousuario",
			{
				params: {
					ACEUSU_ID_MENU_PF: "mni_movimentacao_expressa_pulmao_picking",
					ACEUSU_ID_USUARIO_PF: Number(userID.value),
					ACEUSU_ID_SOFTWARE_NAME_PF: "Harpia_WMS",
				},
			},
		);


		console.log("Data: ", data);

		const companiesForUser = data.values!.companies;

		for (const company of companiesForUser) {
			companiesNames.push(company.DESCR_226);
			companiesID.push(company.EMPRESA_PK_226.toString());
		}

		if (companiesForUser.length > 0) disableDropDown.value = false;
		else disableDropDown.value = true;

		userStore.setCompaniesNames(companiesNames);
		userStore.setCompaniesID(companiesID);

		// Pré-seleciona a última empresa do cookie (por ID ou nome)
		await nextTick();
		aplicarEmpresaDoCookie(companiesNames, companiesID);
	}

	/** Normaliza cookie antigo (só string) e o formato atual `{ id, nome }`. */
	function lerUltimaEmpresa(): UltimaEmpresaCookie | null {
		const raw = ultimaEmpresaCookie.value;
		if (!raw) return null;
		if (typeof raw === "string") {
			const nome = raw.trim();
			return nome ? { id: "", nome } : null;
		}
		if (typeof raw === "object" && raw !== null) {
			return {
				id: String(raw.id ?? "").trim(),
				nome: String(raw.nome ?? "").trim(),
			};
		}
		return null;
	}

	/** Grava no cookie a empresa escolhida (id + nome). */
	function gravarUltimaEmpresa(nome: string) {
		const idx = userStore.companiesNames.indexOf(nome);
		const id = idx >= 0 ? String(userStore.companiesID[idx] ?? "") : "";
		ultimaEmpresaCookie.value = { id, nome };
	}

	/**
	 * Se o cookie `harpia_ultima_empresa` estiver entre as opções carregadas,
	 * preenche o dropdown. Prioridade: ID da empresa → nome.
	 */
	function aplicarEmpresaDoCookie(nomes: string[], ids: string[]) {
		const salva = lerUltimaEmpresa();
		if (!salva) {
			if (companySelected.value && !nomes.includes(companySelected.value)) {
				companySelected.value = "";
			}
			return;
		}

		// 1) Match por ID (mais confiável)
		if (salva.id) {
			const idx = ids.findIndex((id) => String(id) === salva.id);
			if (idx >= 0 && nomes[idx]) {
				companySelected.value = nomes[idx];
				return;
			}
		}

		// 2) Fallback por nome (cookie antigo ou id ausente)
		if (salva.nome && nomes.includes(salva.nome)) {
			companySelected.value = salva.nome;
			return;
		}

		// Empresa salva não está mais disponível para este usuário
		if (companySelected.value && !nomes.includes(companySelected.value)) {
			companySelected.value = "";
		}
	}

	function authenticate() {
		if (!userID.value) {
			return;
		}

		/* 
		 * Validate company selection BEFORE sending request 
		 */
		if (companySelected.value === "") {
			errorModalObject.value.statusCode = "404";
			errorModalObject.value.error = "Selecione uma empresa";
			showErrorModal.value = true;
			return;
		}

		const cookie = useCookie<string | null>("accessToken");

		const idIndex = userStore.companiesNames.indexOf(companySelected.value);
		userStore.setCompanyName(companySelected.value);
		userStore.setCompanyID(Number.parseInt(userStore.companiesID[idIndex]));

		$r3fetchdata
			.post<userAuthResponse>("/api/login", {
				userid: userID.value,
				password: loginForm.inputPassword.value?.inputValue,
				tenant: userStore.companyID,
			})
			.then(async (resp) => {
				if (resp.success && resp.response) {
					if (companySelected.value === "") {
						errorModalObject.value.statusCode = "404";
						errorModalObject.value.error = "Selecione uma empresa";
						showErrorModal.value = true;

					} else {
						cookie.value = resp.response.accessToken;
						if (typeof document !== 'undefined') {
							document.cookie = `accessToken=${resp.response.accessToken}; path=/;`;
						}
						const expiration = getAccessTokenExpiration(resp.response.accessToken);
						if (expiration) {
							cookie.maxAge = Math.max(Math.floor((expiration - Date.now()) / 1000), 1);
						}
						// Grava a empresa usada neste login para o próximo acesso
						gravarUltimaEmpresa(companySelected.value);
						syncSessionFromToken(resp.response.accessToken);
						scheduleExpirationFromToken(resp.response.accessToken);
						useSetFullScreen(true);
						userStore.setAuthStatus(true);
						userStore.setUserID(
							Number.parseInt(
								String(loginForm.inputUser.value?.inputValue || ""),
							),
						);

						/* const play = () => {
							var sound = new Howl({
								src: ['sounds/welcomehpw.mp3'],
								autoplay: true,
								//loop: true,
								volume: 0.5,
								onend: function () {
								console.log('Finished!');
								}
							});
						}
						play(); */
						userStore.setAuthenticationLogout(false);

						await useSetMenusCollector();
						// Menu desktop: filtra rotinas (ex.: 1150) por CORE_ACESSO_USUARIO.
						try {
							await useSetMenusStation();
						}
						catch (e) {
							console.error("[login] falha ao montar menu desktop filtrado:", e);
						}

						await navigateTo("/");
					}
				}
			})
			.catch((error: any) => {
				console.log(error);
				const status = error.response?.status || error.status;
				if (status) {
					console.log(status);
					errorModalObject.value.statusCode = status.toString();
					if (status === 401)
						errorModalObject.value.error = "Usuário não autorizado ou não existe";
					if (status === 500) {
						errorModalObject.value.error =
							"Não foi possível fazer o login, por favor, revise os dados";
					}
					showErrorModal.value = true;
				}
			});
	}

	// Animations
	const loginButtonHover = ref(false);
	const loginButtonRef = ref<HTMLElement>();

	let loginButtonMotionZoomIn: MotionInstance<{
		initial: {
			scale: number;
		};
		hovered: {
			scale: number;
		};
	}>;

	function loginButtonZoomIn() {
		loginButtonMotionZoomIn = useMotion(loginButtonRef, {
			initial: {
				scale: 1,
			},
			hovered: {
				scale: 1.05,
			},
		});
	}

	function setLoginButtonScaleToDefault() {
		loginButtonMotionZoomIn.stop;
		useMotion(loginButtonRef, {
			initial: {
				scale: 1.05,
			},
			enter: {
				scale: 1,
			},

			transition: {
				enter: {
					duration: 800,
				},
			},
		});
	}

	function someErrorLogger(error) {
		console.log(`someErrorLogger${error}`);
	}

	// usuário não autorizado

	const showUnauthorizedUserAlert = ref(false);

	function failModalDismiss() {
		showUnauthorizedUserAlert.value = false;
		userStore.setAuthenticationLogout(false);
	}

	// componentes habilitados

	// const userInputRef = ref<HTMLElement>() as Ref<HTMLElement>;
	// const userPasswordRef = ref<HTMLElement>() as Ref<HTMLElement>;

	// const changeComponentsEnabled = useTemplateUtils([
	// 	{
	// 		name: "userInput",
	// 		ref: userInputRef,
	// 	},
	// 	{
	// 		name: "passwordInput",
	// 		ref: userPasswordRef,
	// 	}
	// ] as const);

	// Armazenar informações do coletor
	function showLoginConfigModal() {
		connectionConfigVisible.value = true;
	}

	function hideLoginConfigModal() {
		connectionConfigVisible.value = false;
	}

	// const nomeColetor =
</script>

<style scoped lang="scss">
	[v-cloak] {
		display: none;
	}

	/* Degradê cinza suave — desktop e coletor (dev 5001) */
	.login-root,
	.login-shell,
	.login-shell--coletor,
	.login-root:has(.login-shell--coletor) {
		background: linear-gradient(
			135deg,
			#e6e8eb 0%,
			#f0f1f3 42%,
			#d9dce1 100%
		);
	}

	/* Shell da tela de login (ocupa a viewport) */
	.login-shell {
		display: flex;
		flex: 1 1 auto;
		width: 100%;
		min-height: 100%;
		min-height: 100dvh;
		align-items: center;
		justify-content: center;
		position: relative;
		overflow: hidden;
	}

	/*
	 * Águia no background (desktop e coletor) — não divide a tela.
	 * Metade esquerda; borda direita da arte no meio (50vw); centro na altura.
	 * Arte branca → silhueta preta no degradê cinza.
	 */
	.login-bg-eagle {
		display: block;
		position: absolute;
		left: 0;
		top: 50%;
		transform: translateY(-50%);
		width: 50vw;
		max-width: 50vw;
		max-height: min(92vh, 36rem);
		height: auto;
		object-fit: contain;
		object-position: right center;
		z-index: 0;
		opacity: 0.28;
		filter: brightness(0);
		user-select: none;
		pointer-events: none;
	}

	.login-body {
		width: 100%;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 1rem;
		padding: 1rem;
		box-sizing: border-box;
		position: relative;
		z-index: 1;
	}

	.login-card {
		width: min(100%, 26rem);
		max-width: 100%;
		background: #ffffff;
		border: 1px solid rgba(15, 23, 42, 0.06);
		border-radius: 6px;
		overflow: hidden;
		display: flex;
		flex-direction: column;
		box-sizing: border-box;
		box-shadow:
			0 1px 2px rgba(15, 23, 42, 0.04),
			0 6px 18px rgba(15, 23, 42, 0.08);
	}

	/* Abaixo do card, centralizado (fora do painel branco) */
	.login-powered {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 0.4rem;
		padding: 0.25rem 0.5rem 0;
		background: transparent;
		border: none;
	}

	.login-powered-text {
		font-size: 0.72rem;
		font-weight: 500;
		letter-spacing: 0.04em;
		text-transform: lowercase;
		color: #6c757d;
		line-height: 1;
	}

	.login-powered-logo {
		display: block;
		height: auto;
		width: auto;
		/* Original ~119×55; um pouco maior no login */
		max-width: 10rem; /* ~160px */
		max-height: 3rem; /* ~48–55px proporcional */
		object-fit: contain;
		user-select: none;
	}

	/*
	 * Padding do formulário — painel próprio, sem Card do PrimeVue.
	 * Margem lateral simétrica garantida.
	 */
	.login-panel-content {
		box-sizing: border-box;
		width: 100%;
		max-width: 100%;
		padding: 1.25rem 1.5rem 1.25rem;
		/* Nunca deixa filho vazar para a borda do card */
		overflow-x: clip;
	}

	.login-fields {
		display: flex;
		flex-direction: column;
		gap: 0.9rem;
		width: 100%;
		max-width: 100%;
		box-sizing: border-box;
	}

	.login-field {
		display: block;
		width: 100%;
		max-width: 100%;
		box-sizing: border-box;
		min-width: 0;
	}

	.user-input,
	.password-input,
	.company-dropdown {
		display: block;
		width: 100%;
		max-width: 100%;
		box-sizing: border-box;
		min-width: 0;
	}

	.login-actions {
		display: flex;
		flex-direction: row;
		align-items: stretch;
		gap: 0.5rem;
		width: 100%;
		max-width: 100%;
		box-sizing: border-box;
	}

	.login-actions-main {
		flex: 1 1 auto;
		min-width: 0;
	}

	/* R3InputNumber / Dropdown: cabem dentro do padding */
	.login-panel-content :deep(.p-inputnumber),
	.login-panel-content :deep(.p-inputtext),
	.login-panel-content :deep(.p-password),
	.login-panel-content :deep(.p-password-input),
	.login-panel-content :deep(.p-dropdown),
	.login-panel-content :deep(.p-button),
	.login-panel-content :deep(.r3-input-number-wrap),
	.login-panel-content :deep(.p-inputgroup),
	.login-panel-content :deep(.p-inputwrapper),
	.login-panel-content :deep(div.w-full) {
		width: 100% !important;
		max-width: 100% !important;
		box-sizing: border-box !important;
	}

	/*
	 * Topo do card = mesmo degradê da topbar (harpia-surfaces / AppTopbar).
	 * Logo branca legível sobre o azul (mix-blend-mode como no desktop).
	 * Padding no título em todos os tamanhos (coletor + desktop).
	 */
	.login-panel-title {
		display: flex;
		align-items: center;
		justify-content: center;
		margin: 0;
		/* Padding do título — coletor e base geral */
		padding: 1.1rem 1.25rem;
		width: 100%;
		max-width: none;
		box-sizing: border-box;
		/* Cobre 100% da largura do card (pai sem padding) */
		background: var(
			--harpia-topbar-gradient,
			linear-gradient(105deg, #05507f 0%, #0772b3 48%, #0a9ee0 100%)
		);
		border-radius: 0;
	}

	/* Logo + versão à direita (mesmo padrão visual da topbar) */
	.login-brand-row {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 0.55rem;
		min-width: 0;
		max-width: 100%;
	}

	.login-logo {
		display: block;
		height: auto;
		width: auto;
		max-width: min(100%, 14rem);
		max-height: 2.4rem;
		object-fit: contain;
		/* Arte com fundo escuro some no degradê; o branco permanece. */
		mix-blend-mode: screen;
		flex-shrink: 1;
	}

	.login-version {
		flex-shrink: 0;
		font-size: 0.78rem;
		font-weight: 600;
		color: #ffffff;
		opacity: 0.95;
		white-space: nowrap;
		line-height: 1;
		padding-top: 0.1rem;
	}

	.username-container label,
	.password-container label,
	.company-container label {
		display: block;
		font-weight: 600;
		font-size: 0.9rem;
		color: #343a40;
		margin-bottom: 0.4rem;
	}

	/* Desktop: formulário e sombra; águia já está no base (só ajusta tamanho) */
	@media screen and (min-width: 721px) {
		.login-bg-eagle {
			max-height: min(96vh, 52rem);
			opacity: 0.3;
		}

		.login-body {
			justify-content: center;
			align-items: center;
			padding: 2.5rem;
			gap: 1.15rem;
		}

		.login-card {
			width: min(100%, 28rem);
			/* Sombra suave para “flutuar” no degradê cinza do fundo */
			box-shadow:
				0 1px 2px rgba(15, 23, 42, 0.05),
				0 8px 24px rgba(15, 23, 42, 0.1),
				0 20px 40px rgba(15, 23, 42, 0.06);
		}

		.login-panel-title {
			padding: 1.5rem 2rem;
		}

		/* Padding simétrico e folgado — esquerda = direita */
		.login-panel-content {
			padding: 1.75rem 2rem 1.75rem;
		}

		.login-fields {
			gap: 1rem;
		}

		.login-powered {
			padding: 0.15rem 0 0;
			gap: 0.45rem;
		}

		.login-powered-logo {
			max-width: 11rem; /* ~176px no desktop */
			max-height: 3.25rem;
		}
	}

	/* Telas largas */
	@media screen and (min-width: 1200px) {
		.login-bg-eagle {
			max-height: min(98vh, 56rem);
			opacity: 0.32;
		}
	}

	@keyframes hoverLoginButtonZoomIn {
		from {
			scale: 1;
		}

		to {
			scale: 1.1;
		}
	}

	@keyframes hoverLoginButtonZoomOut {
		from {
			scale: 1.1;
		}

		to {
			scale: 1;
		}
	}

	.login-panel-content .company-dropdown {
		padding: 0;

		.p-overlay-open {
			box-shadow: none !important;
		}
	}

	.loginButtonAnimationZoomIn {
		animation-name: hoverLoginButton;
		animation-duration: 1s;
	}

	// .p-card-title {
	// 	height: 10vh !important;
	// }

	.loginButtonAnimationZoomOut {
		animation-name: hoerLoginButtonZoomOut;
		animation-duration: 1s;
	}

	/* Coletor / mobile: mesma águia no fundo (metade esquerda, centro na altura) */
	@media screen and (max-width: 720px) {
		.login-bg-eagle {
			display: block;
			width: 50vw;
			max-width: 50vw;
			max-height: min(85vh, 28rem);
			opacity: 0.26;
		}

		.login-panel-title {
			text-align: center;
			/* Título no coletor: padding interno (não zera — deixa o logo “respirar”) */
			padding: 1.15rem 1.1rem !important;
			width: 100% !important;
		}

		.login-logo {
			max-width: min(78%, 12.5rem);
			max-height: 2.15rem;
		}

		.login-version {
			font-size: 0.72rem;
		}

		.login-panel-content {
			padding: 1.15rem 1.35rem 1.15rem;
		}

		.login-card {
			/* elevação um pouco maior no coletor para “flutuar” no degradê cinza do fundo */
			box-shadow:
				0 1px 2px rgba(15, 23, 42, 0.05),
				0 8px 24px rgba(15, 23, 42, 0.1);
		}
	}

	/* Coletor nativo (classe) — mesma ideia de fundo */
	.login-shell--coletor .login-bg-eagle {
		display: block !important;
		opacity: 0.26;
		max-height: min(85vh, 28rem);
	}

	.login-shell--coletor .login-panel-title {
		padding: 1.15rem 1.25rem;
	}

	.login-shell--coletor .login-panel-content {
		padding: 1.15rem 1.35rem 1.15rem;
	}

	@media screen and (max-width: 240px) {
		.login-panel-content {
			margin-top: 5% !important;
		}

		.logo-container {
			.logo-image {
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: center;
			}

			img {
				margin: 20px 0px 0px 0px !important;
			}

			.guest-sign-in {
				display: none;
			}
		}

		// .guest-sign-in {
		// 	display: none;
		// }
	}
</style>
